// ============================================
// Thomas / JT — General Counsel Review Service
// Extracts attorney review issues from generated
// documents and manages guided review sessions.
// ============================================

import type {
  ReviewIssue, ReviewSession,
  ReviewDecision, ReviewPhase, ReviewIssueType,
  DocDocument, DocTemplate, BrandConfig, AuditReport,
} from '../../types/docops';
import { claudeChat, claudeGenerate } from '../ai/providers/claude';

const MODEL = 'claude-sonnet-4-5-20250929';
const TEMPERATURE = 0.3;
const MAX_TOKENS = 512;

// ── Issue Extraction ──

const ATTORNEY_NOTE_RE = /ATTORNEY\s+NOTES?\s*[—:\-]\s*([\s\S]+?)(?=\n##|\n[A-Z]{2,}[A-Z\s]+[—:\-]|\Z)/gi;
const HIGH_PRIORITY_RE = /(?:🔴|HIGH\s+PRIORITY)[:\s]*([\s\S]+?)(?=\n##|\n[A-Z]{2,}|\Z)/gi;
const VARIABLE_RE = /\{\{([^}]+)\}\}/g;
const ATTORNEY_CHECKLIST_RE = /ATTORNEY\s+REVIEW\s+CHECKLIST\s*\n([\s\S]+?)(?=\n##|\Z)/gi;
const SUGGEST_RE = /([a-z][a-zA-Z\s]+?)\s*(?:—|\()\s*suggest[:\s]+(.+?)(?:\)|\n)/gi;

// Issue type constants — used in addIssue calls and sort ordering
const ISSUE_TYPE_CRITICAL: ReviewIssueType = 'critical';
const ISSUE_TYPE_STRUCTURAL: ReviewIssueType = 'structural';
const ISSUE_TYPE_VARIABLE: ReviewIssueType = 'variable';
const ISSUE_TYPE_ORDER: Record<ReviewIssueType, number> = {
  [ISSUE_TYPE_CRITICAL]: 0,
  [ISSUE_TYPE_STRUCTURAL]: 1,
  [ISSUE_TYPE_VARIABLE]: 2,
};

export function extractIssues(content: string, auditReport?: AuditReport): ReviewIssue[] {
  const issues: ReviewIssue[] = [];
  const seen = new Set<string>();
  let idx = 0;

  const addIssue = (type: ReviewIssueType, title: string, description: string, recommendation = '') => {
    const key = `${type}:${title}`;
    if (seen.has(key)) return;
    seen.add(key);
    issues.push({
      id: `issue-${++idx}`,
      type,
      title,
      description,
      recommendation,
      resolved: false,
    });
  };

  // ── Quality Gate Issues (from AuditReport) ──
  // These take priority — they represent programmatic findings from the gate
  if (auditReport && typeof auditReport === 'object') {
    // Missing critical provisions
    if (Array.isArray(auditReport.missingCritical) && auditReport.missingCritical.length > 0) {
      for (const provision of auditReport.missingCritical) {
        if (typeof provision === 'string' && provision.length > 0) {
          addIssue(
            ISSUE_TYPE_CRITICAL,
            `Missing critical: ${provision.slice(0, 60)}`,
            `Quality gate flagged this critical provision as missing from the document.`,
            `Ensure "${provision}" language is present and substantive.`,
          );
        }
      }
    }

    // Missing high-priority provisions
    if (Array.isArray(auditReport.missingHigh) && auditReport.missingHigh.length > 0) {
      for (const provision of auditReport.missingHigh) {
        if (typeof provision === 'string' && provision.length > 0) {
          addIssue(
            ISSUE_TYPE_STRUCTURAL,
            `Missing high: ${provision.slice(0, 60)}`,
            `Quality gate flagged this high-priority provision as missing.`,
            `Review whether "${provision}" should be added or if existing language covers it.`,
          );
        }
      }
    }

    // Entity consistency issues
    if (Array.isArray(auditReport.entityIssues) && auditReport.entityIssues.length > 0) {
      const entityList = auditReport.entityIssues
        .filter((e): e is string => typeof e === 'string' && e.length > 0);
      if (entityList.length > 0) {
        addIssue(
          ISSUE_TYPE_STRUCTURAL,
          `Entity issues (${entityList.length})`,
          entityList.map((e, i) => `${i + 1}. ${e}`).join('\n'),
          'Verify entity names match the entity registry.',
        );
      }
    }
  }

  // ── Content-Based Issues (from document text) ──

  // 1. HIGH PRIORITY items → critical
  let match: RegExpExecArray | null;
  while ((match = HIGH_PRIORITY_RE.exec(content)) !== null) {
    const text = match[1].trim().split('\n')[0].trim();
    if (text.length > 5) {
      addIssue(ISSUE_TYPE_CRITICAL, text.slice(0, 80), match[1].trim());
    }
  }

  // 2. ATTORNEY NOTE blocks → structural
  while ((match = ATTORNEY_NOTE_RE.exec(content)) !== null) {
    const body = match[1].trim();
    const firstLine = body.split('\n')[0].trim();
    if (firstLine.length > 5) {
      addIssue(ISSUE_TYPE_STRUCTURAL, firstLine.slice(0, 80), body);
    }
  }

  // 3. ATTORNEY REVIEW CHECKLIST → consolidate into ONE structural issue
  const checklistItems: string[] = [];
  while ((match = ATTORNEY_CHECKLIST_RE.exec(content)) !== null) {
    const lines = match[1].trim().split('\n').filter(l => l.trim().startsWith('-') || l.trim().startsWith('*'));
    for (const line of lines) {
      const text = line.replace(/^[\s\-\*]+/, '').trim();
      if (text.length > 10) checklistItems.push(text);
    }
  }
  if (checklistItems.length > 0) {
    addIssue(
      ISSUE_TYPE_STRUCTURAL,
      `Review Checklist (${checklistItems.length} items)`,
      checklistItems.map((t, i) => `${i + 1}. ${t}`).join('\n'),
    );
  }

  // 4. Suggested variables → only include non-boilerplate
  while ((match = SUGGEST_RE.exec(content)) !== null) {
    const varName = match[1].trim();
    const suggestion = match[2].trim();
    // Skip short/boilerplate suggestions
    if (suggestion.length > 10) {
      addIssue(ISSUE_TYPE_VARIABLE, varName.slice(0, 80), `Suggested: ${suggestion}`, suggestion);
    }
  }

  // 5. Unresolved {{placeholders}} → consolidate into ONE variable issue
  const placeholders = new Set<string>();
  // Skip brand config vars that should auto-fill
  const brandConfigVars = new Set([
    'brandName', 'brandEntity', 'stateOfFormation', 'pcEntityName',
    'medicalDirectorName', 'mdCredentials', 'trustName', 'telehealthNetwork',
    'companyAddress', 'companyPhone', 'companyEmail', 'ein', 'npi',
  ]);
  while ((match = VARIABLE_RE.exec(content)) !== null) {
    const ph = match[1].trim();
    if (!brandConfigVars.has(ph)) placeholders.add(ph);
  }
  if (placeholders.size > 0) {
    const phList = [...placeholders];
    addIssue(
      ISSUE_TYPE_VARIABLE,
      `Unresolved Placeholders (${phList.length})`,
      phList.map(ph => `- {{${ph}}}`).join('\n'),
    );
  }

  // Sort: critical first, then structural, then variable
  issues.sort((a, b) => ISSUE_TYPE_ORDER[a.type] - ISSUE_TYPE_ORDER[b.type]);

  // Cap at 8 issues max — prioritize critical and structural
  if (issues.length > 8) {
    issues.length = 8;
  }

  return issues;
}

// ── System Prompt Builder ──

function buildSystemPrompt(
  doc: DocDocument,
  config: BrandConfig,
  issues: ReviewIssue[],
  phase: ReviewPhase,
  currentIndex: number,
  decisions: ReviewDecision[],
): string {
  const issueList = issues.map((iss, i) => {
    const status = iss.resolved ? `[RESOLVED: ${iss.resolution}]` : '[OPEN]';
    return `${i + 1}. [${iss.type.toUpperCase()}] ${status} ${iss.title}\n   ${iss.description}${iss.recommendation ? `\n   Recommendation: ${iss.recommendation}` : ''}`;
  }).join('\n\n');

  const decisionList = decisions.map((d, i) =>
    `${i + 1}. Issue "${issues.find(iss => iss.id === d.issueId)?.title ?? d.issueId}": ${d.decision}`
  ).join('\n');

  // Build quality gate context block
  const gateLines: string[] = [];
  const report = doc.auditReport;
  if (report && typeof report === 'object') {
    gateLines.push(`Gate Score: ${report.score ?? '?'}/100 (${report.passed ? 'PASSED' : 'FAILED'})`);
    if (report.fillStage) {
      gateLines.push(`Fill Stage: ${report.fillStage}`);
    }
    if (Array.isArray(report.missingCritical) && report.missingCritical.length > 0) {
      gateLines.push(`Missing Critical: ${report.missingCritical.join(', ')}`);
    }
    if (Array.isArray(report.missingHigh) && report.missingHigh.length > 0) {
      gateLines.push(`Missing High: ${report.missingHigh.join(', ')}`);
    }
    if (report.summary) {
      gateLines.push(`Summary: ${report.summary}`);
    }
  } else {
    gateLines.push('Gate: Not scored');
  }

  return `Thomas ("JT"), junior associate. Attorney review for ${doc.title}.

${config.brandEntity} (${config.stateOfFormation}) | Score: ${doc.auditScore ?? '?'}/100
${gateLines.join('\n')}
Phase: ${phase} | Issue ${currentIndex + 1}/${issues.length}

ISSUES:
${issueList}
${decisions.length > 0 ? `\nDECISIONS:\n${decisionList}` : ''}

════════════════════════════════════════
MANDATORY FORMAT — VIOLATING THIS = FAILURE
════════════════════════════════════════

You MUST write SHORT responses. MAX 3-4 sentences total.

NEVER include: document overviews, entity summaries, key provisions, compliance descriptions, structural points, billing details, recitals of what you built, or ANY background context.

PER ISSUE (your entire message):
**Issue N: [title] ([section])** — [problem]. [recommendation].

CHOICES:
- [rec, 3-8 words]
- [alt, 3-8 words]
- Give me more detail
- Skip for now

AFTER ATTORNEY DECIDES (your entire message):
Got it.

**Issue N: [title] ([section])** — [problem]. [recommendation].

CHOICES:
- [rec]
- [alt]
- Give me more detail
- Skip for now

The CHOICES block is PARSED BY CODE into buttons. It MUST be the last thing in every message. Every message. No exceptions.`;
}

// ── Session Management ──

export async function createReviewSession(
  doc: DocDocument,
  _tpl: DocTemplate,
  config: BrandConfig,
): Promise<{ session: ReviewSession; briefing: string }> {
  const issues = extractIssues(doc.content, doc.auditReport);

  const session: ReviewSession = {
    docId: doc.id,
    documentTitle: doc.title,
    issues,
    decisions: [],
    conversationHistory: [],
    phase: issues.length > 0 ? 'briefing' : 'complete',
    currentIssueIndex: 0,
  };

  const now = new Date().toISOString();

  // Zero issues → hardcoded response, skip API call
  if (issues.length === 0) {
    const score = doc.auditScore ?? '?';
    const briefing = `Hi Counselor, good morning. **${doc.title}** scored ${score}/100 — clean, no issues flagged. Ready for your approval.\n\nCHOICES:\n- Approve for execution\n- Show me the details`;
    session.phase = 'complete';
    session.completedAt = now;
    session.conversationHistory.push({ role: 'thomas', content: briefing, timestamp: now });
    return { session, briefing };
  }

  // Has issues → call API for briefing + Issue 1
  const systemPrompt = buildSystemPrompt(doc, config, issues, 'briefing', 0, []);
  const briefingPrompt = `Your ENTIRE response must follow this EXACT structure — no other content:

Hi Counselor, good morning. **${doc.title}** — ${issues.length} issues before finalization.

**Issue 1: [title] ([section])** — [one sentence problem]. [one sentence recommendation].

CHOICES:
- [your rec]
- [alternative]
- Give me more detail
- Skip for now

DO NOT add: document overviews, entity relationships, key provisions, compliance summaries, structural points, billing info, or ANY other context. The attorney already knows the document.`;

  const briefing = await claudeGenerate(
    MODEL, systemPrompt, briefingPrompt, TEMPERATURE, MAX_TOKENS,
  );

  session.conversationHistory.push({ role: 'thomas', content: briefing, timestamp: now });

  return { session, briefing };
}

export async function sendToThomas(
  doc: DocDocument,
  config: BrandConfig,
  session: ReviewSession,
  attorneyMessage: string,
  resolveIssue = false,
): Promise<{ response: string; updatedSession: ReviewSession }> {
  const updated = { ...session };
  const now = new Date().toISOString();

  // Record attorney message
  updated.conversationHistory = [
    ...updated.conversationHistory,
    { role: 'attorney' as const, content: attorneyMessage, timestamp: now },
  ];

  // Only resolve the current issue if explicitly flagged as a decision
  if (
    resolveIssue &&
    (updated.phase === 'review' || updated.phase === 'variables') &&
    updated.currentIssueIndex < updated.issues.length
  ) {
    const currentIssue = updated.issues[updated.currentIssueIndex];
    if (!currentIssue.resolved) {
      currentIssue.resolved = true;
      currentIssue.resolution = attorneyMessage;
      updated.decisions = [
        ...updated.decisions,
        { issueId: currentIssue.id, decision: attorneyMessage, timestamp: now },
      ];
    }
    // Advance to next unresolved issue
    let nextIdx = updated.currentIssueIndex + 1;
    while (nextIdx < updated.issues.length && updated.issues[nextIdx].resolved) {
      nextIdx++;
    }
    updated.currentIssueIndex = nextIdx;

    // Check if all issues resolved
    if (updated.issues.every(i => i.resolved)) {
      updated.phase = 'consolidation';
    }
  } else if (updated.phase === 'briefing') {
    // After first attorney response, move to review
    updated.phase = 'review';
  } else if (updated.phase === 'consolidation') {
    // Attorney confirmed consolidation → complete
    updated.phase = 'complete';
    updated.completedAt = now;
  }

  // Build conversation history for Claude
  const history = updated.conversationHistory.map(m => ({
    role: m.role === 'thomas' ? 'assistant' as const : 'user' as const,
    content: m.content,
  }));

  const systemPrompt = buildSystemPrompt(
    doc, config, updated.issues, updated.phase, updated.currentIssueIndex, updated.decisions,
  );

  let userPrompt: string;
  if (updated.phase === 'consolidation') {
    userPrompt = 'All issues are resolved. Present a numbered summary of every decision made during this session. Ask the attorney to confirm so you can generate the final version.';
  } else if (updated.phase === 'complete') {
    userPrompt = 'The attorney has confirmed. Summarize the session and let them know the final document is ready to generate. Professional close.';
  } else {
    userPrompt = attorneyMessage;
  }

  // Use claudeChat with full history for multi-turn
  const response = await claudeChat(
    MODEL, systemPrompt,
    history.slice(0, -1), // history without the last user message (claudeChat adds it)
    userPrompt,
    TEMPERATURE, MAX_TOKENS,
  );

  updated.conversationHistory = [
    ...updated.conversationHistory,
    { role: 'thomas' as const, content: response, timestamp: new Date().toISOString() },
  ];

  return { response, updatedSession: updated };
}

// ── Final Document Generation ──

export async function generateFinalDocument(
  doc: DocDocument,
  config: BrandConfig,
  session: ReviewSession,
): Promise<string> {
  const decisionList = session.decisions.map((d, i) => {
    const issue = session.issues.find(iss => iss.id === d.issueId);
    return `${i + 1}. ${issue?.title ?? d.issueId}: ${d.decision}`;
  }).join('\n');

  const configEntries = Object.entries(config)
    .filter(([, v]) => typeof v === 'string' && v.length > 0)
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n');

  const prompt = `You are regenerating this legal document with all attorney decisions applied.

ORIGINAL DOCUMENT:
${doc.content}

ATTORNEY DECISIONS:
${decisionList}

BRAND CONFIG VALUES:
${configEntries}

INSTRUCTIONS:
1. Apply every attorney decision to the document
2. REMOVE all "ATTORNEY NOTE" blocks entirely
3. REMOVE the "VARIABLES REQUIRING ATTORNEY INPUT" section
4. REMOVE the "ATTORNEY REVIEW CHECKLIST" section
5. REMOVE all HIGH PRIORITY markers and 🔴 indicators
6. REPLACE all {{variable}} placeholders with actual values from Brand Config or attorney decisions
7. REPLACE all "suggest: X" placeholders with decided values
8. Keep all legal substance intact — only remove review scaffolding
9. Output a clean, signature-ready legal document
10. Maintain section numbering, defined terms, and cross-references
11. End with signature blocks (no trailing notes or commentary)

Output the complete final document in markdown format.`;

  return claudeGenerate(MODEL, '', prompt, 0.1, 8192);
}
